import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, Fragment } from "react";
import { collection, doc, getDoc, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { CalendarIcon, ChevronDownIcon, ChevronUpIcon, UserGroupIcon, PhotoIcon } from "@heroicons/react/20/solid";
import { getEventStatus } from "../../utils/eventStatus";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";

export default function Hosts({ embedded = false }) {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [expandedVendorId, setExpandedVendorId] = useState(null);
  const [vendorEvents, setVendorEvents] = useState({});
  const [loadingEvents, setLoadingEvents] = useState({});

  useEffect(() => {
    const fetchAllVendors = async () => {
      try {
        const vendorCollection = collection(db, "vendors");
        const querySnapshot = await getDocs(vendorCollection);
        const vendorsList = querySnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        const vendorsWithUsers = await Promise.all(
          vendorsList.map(async (vendor) => {
            try {
              const userDoc = await getDoc(doc(db, "users", vendor.id));
              return {
                ...vendor,
                user: userDoc.exists() ? userDoc.data() : null,
              };
            } catch {
              return { ...vendor, user: null };
            }
          })
        );
        setVendors(vendorsWithUsers);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    };
    fetchAllVendors();
  }, []);

  const fetchEventsForVendor = useCallback(async (vendorId) => {
    if (vendorEvents[vendorId]) return; // Already fetched
    setLoadingEvents(prev => ({ ...prev, [vendorId]: true }));
    try {
      const eventCollection = collection(db, "events");
      const q = query(eventCollection, where("vendorID", "==", vendorId), orderBy("date", "desc"));
      const querySnapshot = await getDocs(q);
      const events = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setVendorEvents(prev => ({ ...prev, [vendorId]: events }));
    } catch (error) {
      console.error("Error fetching events for vendor:", error);
      setVendorEvents(prev => ({ ...prev, [vendorId]: [] }));
    } finally {
      setLoadingEvents(prev => ({ ...prev, [vendorId]: false }));
    }
  }, [vendorEvents]);

  const handleRowClick = (vendorId) => {
    if (expandedVendorId === vendorId) {
      setExpandedVendorId(null);
    } else {
      setExpandedVendorId(vendorId);
      fetchEventsForVendor(vendorId);
    }
  };

  const tableContent = (
    <div className="mt-8 flow-root mx-auto p-6 bg-white dark:bg-gray-800 rounded-md shadow-md">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 sm:pl-0">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Stripe Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Super Admin
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {vendors.map((vendor) => {
                  const isExpanded = expandedVendorId === vendor.id;
                  const events = vendorEvents[vendor.id] || [];
                  const isLoadingEvents = loadingEvents[vendor.id] || false;
                  return (
                    <Fragment key={vendor.id}>
                      <tr
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        onClick={() => handleRowClick(vendor.id)}
                      >
                        <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-0">
                          <div className="flex items-center gap-x-3">
                            {isExpanded ? (
                              <ChevronUpIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                            )}
                            {vendor.user?.photoURL ? (
                              <img
                                src={variantURL(vendor.user.photoURL, 200)}
                                onError={fallbackToOriginal(vendor.user.photoURL)}
                                alt={vendor.user.name}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                            )}
                            <div>
                              <div>{vendor.user?.name || "—"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500 dark:text-gray-400">
                          {vendor.stripeAccountStatus ? (
                            <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                              vendor.stripeAccountStatus === "active"
                                ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                                : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
                            }`}>
                              {vendor.stripeAccountStatus}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500 dark:text-gray-400">
                          {vendor.isSuperAdmin ? (
                            <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300">
                              Yes
                            </span>
                          ) : (
                            "No"
                          )}
                        </td>
                        <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/hosts/${vendor.id}/edit`); }}
                            className="text-primary hover:text-indigo-900 dark:hover:text-indigo-300"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={4} className="px-4 py-4 bg-gray-50 dark:bg-gray-800">
                            {isLoadingEvents ? (
                              <div className="flex items-center justify-center py-6">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2"></div>
                                <span className="text-sm text-gray-500 dark:text-gray-400">Loading events...</span>
                              </div>
                            ) : events.length === 0 ? (
                              <div className="text-center py-6">
                                <CalendarIcon className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">No events hosted by this host.</p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                {events.map((event) => {
                                  const eventStatus = getEventStatus(event);
                                  return (
                                    <div
                                      key={event.id}
                                      onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.id}/edit`); }}
                                      className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${eventStatus.status === 'completed' ? 'opacity-50' : ''}`}
                                    >
                                      {event.photos && event.photos.length > 0 && (
                                        <div className="relative h-32 bg-gray-100 dark:bg-gray-800">
                                          <img
                                            src={variantURL(event.photos[0], 800)}
                                            onError={fallbackToOriginal(event.photos[0])}
                                            alt={event.name}
                                            className="w-full h-full object-cover"
                                          />
                                          <div className="absolute top-2 right-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${eventStatus.color}`}>
                                              {eventStatus.label}
                                            </span>
                                          </div>
                                          {event.photos.length > 1 && (
                                            <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-0.5 rounded-full flex items-center">
                                              <PhotoIcon className="h-3 w-3 mr-1" />
                                              +{event.photos.length - 1}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      <div className="p-3">
                                        <div className="flex items-start justify-between">
                                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-1">{event.name}</h4>
                                          {(!event.photos || event.photos.length === 0) && (
                                            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${eventStatus.color}`}>
                                              {eventStatus.label}
                                            </span>
                                          )}
                                        </div>
                                        <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                                          <CalendarIcon className="h-3.5 w-3.5 mr-1 text-gray-400 dark:text-gray-500" />
                                          {event.date.toDate().toLocaleDateString()} {event.date.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                        {event.hostname && (
                                          <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                                            <UserGroupIcon className="h-3.5 w-3.5 mr-1 text-gray-400 dark:text-gray-500" />
                                            {event.hostname}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );

  if (embedded) return tableContent;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex mt-4 sm:mt-0 justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Hosts</h1>
      </div>
      {tableContent}
    </div>
  );
}
